// src/components/commandSearch/commandSearchUtils.js
//
// Command Search Utilities
// ========================
//
// PURPOSE
// -------
// Small pure helper functions used by Command Search.
//
// Keeping these helpers outside React components makes the feature easier to
// test and prevents Navbar from owning search-specific behavior.

export function normalizeSearchText(value) {
  return String(value || "").trim().toLowerCase();
}

export function canUseCommand(item, user) {
  const roles = Array.isArray(item?.roles) ? item.roles : [];

  if (!roles.length) return true;

  const userRole = String(user?.role || "")
    .trim()
    .toLowerCase();

  const normalizedRoles = roles.map((role) =>
    String(role || "")
      .trim()
      .toLowerCase()
  );

  return normalizedRoles.includes(userRole);
}

export function filterStaticCommands(items, query, user) {
  const q = normalizeSearchText(query);

  const roleVisible = (Array.isArray(items) ? items : []).filter((item) =>
    canUseCommand(item, user)
  );

  if (!q) return roleVisible;

  return roleVisible.filter((item) => {
    const haystack = [
      item.group,
      item.title,
      item.subtitle,
      item.type,
      ...(Array.isArray(item.keywords) ? item.keywords : []),
    ]
      .join(" ")
      .toLowerCase();

    return haystack.includes(q);
  });
}

export function groupCommandResults(items) {
  const groups = [];

  for (const item of Array.isArray(items) ? items : []) {
    const groupName = item.group || "Results";
    let group = groups.find((g) => g.name === groupName);

    if (!group) {
      group = { name: groupName, items: [] };
      groups.push(group);
    }

    group.items.push(item);
  }

  return groups;
}

export function normalizeBackendSearchResult(row) {
  if (!row || typeof row !== "object") return null;

  /*
    Backend result normalization
    ----------------------------
    Preferred backend response shape:

      {
        id: "voucher:123",
        type: "voucher",
        group: "Vouchers",
        title: "Voucher 2026/00123",
        subtitle: "Anna Nowak · active",
        path: "/vouchers/123",
        score: 0.91
      }

    Defensive aliases are supported because backend search may initially return
    slightly different naming:
    - entityId instead of id
    - to/url instead of path
    - label/name instead of title
    - description instead of subtitle
  */

  const type = String(row.type || "result").trim() || "result";

  const path =
    row.path ||
    row.to ||
    row.url ||
    row.action?.path ||
    row.action?.to ||
    "";

  const entityId =
    row.entityId ||
    row.entity_id ||
    row.recordId ||
    row.record_id ||
    "";

  const id =
    row.id ||
    (entityId ? `${type}:${entityId}` : "") ||
    `${type}:${path || row.title || row.label || row.name || "unknown"}`;

  const title =
    row.title ||
    row.label ||
    row.name ||
    row.displayName ||
    "Untitled result";

  const subtitle =
    row.subtitle ||
    row.description ||
    row.meta ||
    "";

  return {
    id,
    type,
    group: row.group || row.category || "Results",
    title,
    subtitle,
    path,
    action: row.action || null,
    score: Number(row.score) || 0,
    source: "backend",
    raw: row,
  };
}
